/**
 * @fileoverview ReportingTable component for rendering dynamic tables with proper TypeScript types
 */

import React from "react"
import type { TransformedTableData } from "utils/convertDataToTable"

/**
 * Props for the ReportingTable component
 */
interface ReportingTableProps {
  /** The transformed data structure for the table */
  transformedData: TransformedTableData
}

/**
 * Style definitions for table cells
 */
interface CellStyles {
  /** Base cell styling */
  cell: React.CSSProperties
  /** Header cell styling */
  header: React.CSSProperties
  /** Subheader cell styling */
  subHeader: React.CSSProperties
}

/**
 * ReportingTable component renders a dynamic table based on the provided data.
 * It handles various table structures including those with and without subheaders,
 * and special cases with top-left headers.
 */
const ReportingTable: React.FC<ReportingTableProps> = ({ transformedData }) => {
  const { headers, rows, hasSubHeaders } = transformedData

  /**
   * Determines if there's a special header for the top-left cell
   */
  const hasTopLeftHeader: boolean =
    hasSubHeaders &&
    headers.length > 0 &&
    headers[0].subHeaders &&
    headers[0].subHeaders.length === 2 &&
    headers[0].subHeaders[0] === "" &&
    headers[0].subHeaders[1] === "Statistic"

  /**
   * Predefined styles for different cell types.
   *
   * minWidth keeps narrow numeric columns readable; maxWidth caps long labels
   * so they wrap rather than stretching the whole table. overflowWrap handles
   * unbroken tokens. The previous whiteSpace: nowrap forced every column to
   * accommodate its longest header on a single line, which made tables
   * impossibly wide for survey questions used as column headers.
   */
  const baseCellStyle: React.CSSProperties = {
    border: "1px solid black",
    padding: "6px 8px",
    textAlign: "center",
    minWidth: "80px",
    maxWidth: "260px",
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    verticalAlign: "middle",
  }

  const styles: CellStyles = {
    cell: baseCellStyle,
    header: {
      ...baseCellStyle,
      backgroundColor: "#f0f0f0",
      fontWeight: "bold",
    },
    subHeader: {
      ...baseCellStyle,
      backgroundColor: "#f7f7f7",
      fontWeight: "bold",
    },
  }

  /**
   * Gets the appropriate cell style based on position and type
   */
  const getCellStyle = (isFirstColumn: boolean): React.CSSProperties => ({
    ...styles.cell,
    backgroundColor: isFirstColumn ? "#f0f0f0" : "#ffffff",
    fontWeight: isFirstColumn ? "bold" : "normal",
  })

  /**
   * Renders the main header row
   */
  const renderMainHeaderRow = () => (
    <tr>
      {hasSubHeaders && (
        <th rowSpan={2} style={styles.header}>
          {hasTopLeftHeader ? String(headers[0].header || "") : ""}
        </th>
      )}
      {hasSubHeaders && (
        <th rowSpan={2} style={styles.header}>
          Statistic
        </th>
      )}
      {headers.slice(hasTopLeftHeader ? 1 : 0).map((group, groupIndex) => (
        <th
          key={groupIndex}
          colSpan={hasSubHeaders ? group.subHeaders.length : 1}
          style={styles.header}
        >
          {String(group.header || "")}
        </th>
      ))}
    </tr>
  )

  /**
   * Renders the subheader row if subheaders exist
   */
  const renderSubHeaderRow = () => {
    if (!hasSubHeaders) return null

    return (
      <tr>
        {headers.slice(hasTopLeftHeader ? 1 : 0).flatMap((group, groupIndex) =>
          group.subHeaders.map((subHeader, subHeaderIndex) => (
            <th
              key={`${groupIndex}-${subHeaderIndex}`}
              style={styles.subHeader}
            >
              {subHeader}
            </th>
          ))
        )}
      </tr>
    )
  }

  /**
   * Renders a single data row
   */
  const renderDataRow = (row: (typeof rows)[0], rowIndex: number) => (
    <tr key={rowIndex}>
      {row.map((cell, cellIndex) => {
        const isFirstColumn = cellIndex === 0
        return (
          <td key={cellIndex} style={getCellStyle(isFirstColumn)}>
            {String(cell ?? "")}
          </td>
        )
      })}
    </tr>
  )

  return (
    <div style={{ overflowX: "auto" }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          tableLayout: "auto",
        }}
      >
        <thead>
          {renderMainHeaderRow()}
          {renderSubHeaderRow()}
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => renderDataRow(row, rowIndex))}
        </tbody>
      </table>
    </div>
  )
}

export default ReportingTable
